import { v4 as uuidv4 } from 'uuid';
import { ParsedModifierGroup } from '../types/menu';
import appContainer from './container';
import { RootStore } from '../app/store';
import {
  InfoTransmissionMessage,
  messagingActions,
} from '../reducers/messagingSlice';
import { UPSELL_METRIC_VERSION } from '../constants';
import { IUpsellExperimentStamp } from '../redux/features/experiments/experiments.props';
import { buildUpsellExperimentStamp } from '../redux/features/experiments/experiments.utils';

interface IUpsellAttempted extends IUpsellExperimentStamp {
  upsell_id: string;
  upsell_intent: string;
  upsell_issued_timestamp: string;
  upsell_phrase: string;
  trigger_by: string;
  upsell_default_item: string;
  upsell_items: string[];
  upsell_decline_items: string[];
  parent_item: string;
}

// The attempt's A/B labels, carried onto every metric that settles it.
const experimentStampOf = ({
  ab_experiment,
  ab_group,
  prompt_variant,
  experiments_unknown,
}: IUpsellExperimentStamp): IUpsellExperimentStamp => ({
  ...(ab_experiment !== undefined && { ab_experiment }),
  ...(ab_group !== undefined && { ab_group }),
  ...(prompt_variant !== undefined && { prompt_variant }),
  ...(experiments_unknown !== undefined && { experiments_unknown }),
});

// HITL-1737: an attempt as it stood when it was made — enough to make it the
// open attempt again.
interface IHeldAttempt {
  attempt: IUpsellAttempted;
  constraintName: string | null;
  offeredCartItemId: string | null;
}

// Sends whose echo never comes back (out of session, a dropped socket) are
// never removed by their echo; the oldest is dropped past this many.
const MAX_AWAITING_ECHO = 10;

export class Upsell {
  upsellAttempted: IUpsellAttempted | null = null;
  upsellSettled: boolean = false;
  // HITL-1701: the constraint behind the current attempt — the key the AI's own
  // upsell_attempted carries as upsell_intent. Kept off IUpsellAttempted so the
  // wire payload is unchanged.
  attemptedConstraintName: string | null = null;
  // HITL-1701: the AI armed its own attempt for this same constraint after ours
  // was made, so it records the customer's answer and ours would count it twice.
  answerRecordedByAI: boolean = false;
  // HITL-1738: the cart item the open attempt was offered on, when there is
  // one. Deleting that item is the only cart edit that closes the offer. Kept
  // off IUpsellAttempted so the wire payload is unchanged.
  offeredCartItemId: string | null = null;
  // HITL-1737: attempts whose prompt went out with an upsell_id and whose
  // ORDERBOARD echo has not come back yet. An agent who presses Enter again
  // replaces the open attempt before the previous line's echo arrives, so the
  // echo is matched here rather than against the open attempt.
  private awaitingEcho = new Map<string, IHeldAttempt>();
  // HITL-1737: the last attempt the customer heard — what a blocked re-send of
  // the same upsell falls back to.
  private lastHeard: IHeldAttempt | null = null;
  // Attempts that have been answered (or settled), and those whose answer the
  // AI records: neither may be reopened as if untouched.
  private answered = new WeakSet<IUpsellAttempted>();
  private recordedByAI = new WeakSet<IUpsellAttempted>();
  getUpsellAttempted() {
    return this.upsellAttempted;
  }
  setUpsellAttempted(value: IUpsellAttempted | null) {
    this.upsellAttempted = value;
    this.attemptedConstraintName = null;
    this.answerRecordedByAI = false;
    this.offeredCartItemId = null;
    // Cleared means the order moved on: nothing heard earlier is reopened.
    if (value === null) this.lastHeard = null;
  }
  getUpsellSettled() {
    return this.upsellSettled;
  }
  setUpsellSettled(value: boolean) {
    this.upsellSettled = value;
    if (value && this.upsellAttempted) this.answered.add(this.upsellAttempted);
  }
  attempt(
    constraint: ParsedModifierGroup,
    parentItem: string,
    triggerBy: string,
    prompt: string,
    upsellName: string,
    upsellId: string = uuidv4(),
    // Session A/B stamp from selectUpsellExperimentStamp; defaults to unknown.
    experimentStamp: IUpsellExperimentStamp = buildUpsellExperimentStamp(null),
    // HITL-1738: the cart item the offer is about, if any.
    offeredCartItemId?: number | string
  ) {
    // HITL-1738: asking a different upsell closes the unanswered one. A re-send
    // of the same upsell does not — that is HITL-1737's case.
    if (
      this.upsellAttempted &&
      (constraint.name ?? null) !== this.attemptedConstraintName
    ) {
      this.settle();
    }
    const {
      upsell_items = [],
      upsell_decline_items = [],
      upsell_default_item = '',
    } = constraint;
    const upsellAttempted = {
      upsell_id: upsellId,
      upsell_issued_timestamp: new Date().toISOString(),
      upsell_phrase: prompt,
      trigger_by: triggerBy,
      upsell_intent: upsellName,
      upsell_default_item,
      upsell_items,
      upsell_decline_items,
      parent_item: parentItem,
      ...experimentStamp,
    };
    this.setUpsellAttempted(upsellAttempted);
    this.attemptedConstraintName = constraint.name ?? null;
    this.offeredCartItemId =
      offeredCartItemId === undefined ? null : String(offeredCartItemId);
    this.setUpsellSettled(false);
    this.awaitingEcho.set(upsellId, {
      attempt: upsellAttempted,
      constraintName: this.attemptedConstraintName,
      offeredCartItemId: this.offeredCartItemId,
    });
    if (this.awaitingEcho.size > MAX_AWAITING_ECHO) {
      this.awaitingEcho.delete(this.awaitingEcho.keys().next().value as string);
    }
    return upsellAttempted;
  }
  /**
   * HITL-1737: the ORDERBOARD played the send carrying this upsell_id. Returns
   * its attempt, for the upsell_attempted METRIC, or undefined when this
   * instance never made it. Echoes come back in send order, so a heard send is
   * the newest one the customer heard: for the same upsell it becomes the open
   * attempt, even when a quicker re-send has already replaced it — the
   * customer is answering what they heard.
   */
  acknowledgeHeard(upsellId: string): IUpsellAttempted | undefined {
    const held =
      this.awaitingEcho.get(upsellId) ??
      (this.upsellAttempted?.upsell_id === upsellId
        ? {
            attempt: this.upsellAttempted,
            constraintName: this.attemptedConstraintName,
            offeredCartItemId: this.offeredCartItemId,
          }
        : undefined);
    if (!held) return undefined;
    this.awaitingEcho.delete(upsellId);
    this.lastHeard = held;
    const open = this.upsellAttempted;
    if (
      open &&
      open !== held.attempt &&
      !this.answered.has(held.attempt) &&
      this.isSameUpsell(held)
    ) {
      this.reopen(held);
    }
    return held.attempt;
  }
  /**
   * HITL-1737: the ORDERBOARD blocked the send carrying this upsell_id — the
   * customer never heard it. Only that attempt is dropped; when it was the
   * open one, the last heard, unanswered attempt for the same upsell takes its
   * place.
   */
  acknowledgeBlocked(upsellId: string) {
    this.awaitingEcho.delete(upsellId);
    if (this.upsellAttempted?.upsell_id !== upsellId) return;
    const heard = this.lastHeard;
    if (
      heard &&
      heard.attempt.upsell_id !== upsellId &&
      !this.answered.has(heard.attempt) &&
      this.isSameUpsell(heard)
    ) {
      this.reopen(heard);
    } else {
      this.setUpsellAttempted(null);
    }
  }
  private isSameUpsell(held: IHeldAttempt) {
    return (
      held.constraintName !== null &&
      held.constraintName === this.attemptedConstraintName
    );
  }
  private reopen({ attempt, constraintName, offeredCartItemId }: IHeldAttempt) {
    this.upsellAttempted = attempt;
    this.attemptedConstraintName = constraintName;
    this.offeredCartItemId = offeredCartItemId;
    this.answerRecordedByAI = this.recordedByAI.has(attempt);
    this.upsellSettled = this.answered.has(attempt);
  }
  /**
   * HITL-1701: the AI sent an upsell_attempted of its own. When it is for the
   * constraint our open attempt was made for, the AI is now recording the same
   * offer — it scores the customer's answer itself — so ours stops recording
   * one. Returns whether the open attempt was handed to the AI.
   */
  markAttemptedByAI(constraintName: string | undefined): boolean {
    if (
      !this.upsellAttempted ||
      this.upsellSettled ||
      !constraintName ||
      constraintName !== this.attemptedConstraintName
    ) {
      return false;
    }
    this.answerRecordedByAI = true;
    this.recordedByAI.add(this.upsellAttempted);
    return true;
  }
  reject(upsellDeclinedItems?: string[]) {
    if (this.upsellAttempted && this.answerRecordedByAI) {
      this.setUpsellSettled(true);
      return undefined;
    }
    if (this.upsellAttempted) {
      const {
        upsell_id,
        upsell_issued_timestamp,
        upsell_intent,
        parent_item,
        upsell_default_item = '',
        upsell_phrase,
      } = this.upsellAttempted;
      this.setUpsellSettled(true);
      return {
        upsell_id,
        upsell_issued_timestamp,
        upsell_intent,
        parent_item,
        upsell_phrase,
        upsell_declined_items: upsellDeclinedItems
          ? upsellDeclinedItems
          : [upsell_default_item],
        cx_utterance: '', // for data schema consistency as AI will send this field
        is_upsell_attempted: 'yes',
        is_upsell_accepted: 'no',
        ...experimentStampOf(this.upsellAttempted),
      };
    }
  }
  accept(upsellAcceptedItems: string[]) {
    if (this.upsellAttempted && this.answerRecordedByAI) {
      this.setUpsellSettled(true);
      return undefined;
    }
    if (this.upsellAttempted) {
      const {
        upsell_id,
        upsell_issued_timestamp,
        upsell_intent,
        parent_item,
        upsell_phrase,
      } = this.upsellAttempted;
      this.setUpsellSettled(true);
      return {
        upsell_id,
        upsell_issued_timestamp,
        upsell_intent,
        parent_item,
        upsell_phrase,
        upsell_accepted_items: upsellAcceptedItems,
        cx_utterance: '', // for data schema consistency as AI will send this field
        is_upsell_attempted: 'yes',
        is_upsell_accepted: 'yes',
        ...experimentStampOf(this.upsellAttempted),
      };
    }
  }

  settle() {
    if (
      this.upsellAttempted &&
      !this.upsellSettled &&
      !this.answerRecordedByAI
    ) {
      // Upsell is rejected implicitly
      const upsell_declined = this.reject();
      const store = appContainer.get<RootStore>('store');
      const message = 'Auto reject upsell';
      const payload: Partial<InfoTransmissionMessage> = {
        data: {
          type: 'METRIC',
          message: message,
          metadata: {
            upsell_declined,
            cx_utterance: '', // for data schema consistency as AI will send this field
            upsell_metric_version: UPSELL_METRIC_VERSION,
          },
        },
      };
      store?.dispatch(messagingActions.sendInfo(payload as any));
    }
    this.setUpsellAttempted(null);
  }
  /**
   * HITL-1738: the agent deleted a cart item. Only deleting the item the open
   * attempt was offered on closes it (as declined) — every other cart edit
   * leaves the offer open for the customer's answer.
   */
  settleIfOffered(cartItemId: number | string) {
    if (
      this.offeredCartItemId !== null &&
      this.offeredCartItemId === String(cartItemId)
    ) {
      this.settle();
    }
  }
}

export const upsell = new Upsell();
